import { clsx } from 'clsx';

interface ErrorBannerAction {
  label:     string;
  onClick:   () => void;
}

interface ErrorBannerProps {
  error:      unknown;
  action?:    ErrorBannerAction;
  className?: string;
}

interface StructuredApiError {
  status: number;
  code?: string;
  detail: string | readonly string[];
}

interface PlainBannerError {
  title:  string;
  detail: string;
}

function isStructuredApiError(error: unknown): error is StructuredApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'detail' in error &&
    typeof (error as { status: unknown }).status === 'number'
  );
}

// Lets callers report a failure that isn't a thrown/caught Error - e.g. a 200 response
// whose payload itself describes a failed state (an integrity check, a validation result).
function isPlainBannerError(error: unknown): error is PlainBannerError {
  return (
    typeof error === 'object' &&
    error !== null &&
    typeof (error as { title: unknown }).title === 'string' &&
    typeof (error as { detail: unknown }).detail === 'string'
  );
}

function getErrorMessage(error: unknown): { title: string; detail: string; code?: string } {
  if (isStructuredApiError(error)) {
    return {
      title:  `Error ${error.status}`,
      detail: Array.isArray(error.detail) ? error.detail.join(' · ') : String(error.detail),
      code:   error.code,
    };
  }
  if (isPlainBannerError(error)) {
    return { title: error.title, detail: error.detail };
  }
  if (error instanceof Error) {
    return { title: 'Error', detail: error.message };
  }
  return { title: 'Unknown error', detail: 'An unexpected error occurred.' };
}

export function ErrorBanner({ error, action, className }: ErrorBannerProps) {
  const { title, detail, code } = getErrorMessage(error);

  return (
    <div
      role="alert"
      className={clsx('rounded-md border p-4', className)}
      style={{
        backgroundColor: 'var(--nwm-status-error-bg)',
        borderColor:     'var(--nwm-status-error-ring)',
        color:           'var(--nwm-status-error-text)',
      }}
    >
      <div className="flex gap-3">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="mt-0.5 h-5 w-5 shrink-0 opacity-70" aria-hidden="true">
          <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
        <div className="min-w-0">
          <p className="text-sm font-semibold">
            {title}
            {code && (
              <span className="ml-2 font-mono text-xs opacity-70">{code}</span>
            )}
          </p>
          <p className="mt-0.5 text-sm opacity-80">{detail}</p>
          {action && (
            <button
              type="button"
              onClick={action.onClick}
              className="mt-2 text-sm font-semibold underline underline-offset-2 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
              style={{ color: 'var(--nwm-status-error-text)' }}
            >
              {action.label} →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
